import {BrowserModule} from '@angular/platform-browser';
// Import Sentry
import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {CommonModule, registerLocaleData} from '@angular/common';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FlexLayoutModule} from '@angular/flex-layout';
import {HeaderModule} from './header/header.module';
import {StoreModule} from '@ngrx/store';
import {metaReducers, reducers} from './reducers';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {environment} from '../environments/environment';
import {PagesModule} from './pages/pages.module';
import {FooterComponent} from './footer/footer.component';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {NgxMaskModule} from 'ngx-mask';
import {GooglePlaceModule} from 'ngx-google-places-autocomplete';
import {EffectsModule} from '@ngrx/effects';
import {RegisterEffects} from './auth/register/effects/register.effects';
import {NgxUiLoaderModule, NgxUiLoaderRouterModule} from 'ngx-ui-loader';
import * as fromLogin from './auth/login/reducers/login.reducer';
import {LoginEffects} from './auth/login/effects/login.effects';
import {CookieService} from 'ngx-cookie-service';
import * as fromCalculator from './calculator/calculator/calculator.reducer';
import {CalculatorEffects} from './calculator/calculator/calculator.effects';
import * as fromOrders from './dashboard/orders/orders.reducer';
import {OrdersEffects} from './dashboard/orders/orders.effects';
import he from '@angular/common/locales/he';
import {MomentModule} from 'ngx-moment';
import {InViewportModule} from 'ng-in-viewport';
import {CountUpModule} from 'ngx-countup';
import {MatIconModule} from '@angular/material/icon';
import {FreightSharedModule} from './freight-shared/freight-shared.module';
import {BottomNavModule} from 'ngx-bottom-nav';
import {SidebarModule} from 'ng-sidebar';
import {NgcCookieConsentConfig, NgcCookieConsentModule} from 'ngx-cookieconsent';
import {ShareIconsModule} from 'ngx-sharebuttons/icons';
import {AgmCoreModule} from '@agm/core';
import * as fromNotification from './reducers/notification/notification.reducer';
import {NotificationEffects} from './reducers/notification/notification.effects';
import * as fromConversation from './reducers/chatmessages/conversation.reducer';
import {ConversationEffects} from './reducers/chatmessages/conversation.effects';
import {ToastModule} from 'primeng/toast';

import {
  NbButtonModule,
  NbCardModule,
  NbDialogModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutDirection,
  NbLayoutModule,
  NbThemeModule,
  NbToggleModule,
  NbTooltipModule
} from '@nebular/theme';
import {NbEvaIconsModule} from '@nebular/eva-icons';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {cookies} from './header/cookies/accept-terms-mobile.component';
// Import Bugsnag
import {Router} from '@angular/router';
import * as Sentry from '@sentry/angular';
import {ErrorInterceptor} from './interceptors/error.interceptor';
import {LanguageInterceptor} from './interceptors/language.interceptor';
import {TermsComponent} from './terms/terms.component';

// import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {TokenInterceptor} from './auth/token.interceptor';

import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgxSliderModule} from '@angular-slider/ngx-slider';
import {VehicleEuStandardInfoComponent} from './vehicle-eu-standard-info/vehicle-eu-standard-info.component';
import {MatInputModule} from '@angular/material/input';
import {MatTableModule} from '@angular/material/table';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatSelectModule} from '@angular/material/select';
import {DropdownModule} from 'primeng/dropdown';
import {MatSortModule} from '@angular/material/sort';
import {AcceptTermsEuComponent} from './vehicle-eu-standard-info/accept-terms-eu/accept-terms-eu.component';
import {MatButtonModule} from '@angular/material/button';
import {CarsMarketLoginComponent} from './cars-market/cars-market-login/login.component';
import {RecaptchaLoaderService, RecaptchaModule} from 'ng-recaptcha';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TextEditableComponent} from './directives/ext-editable.directive';
import {CarsGridComponent} from './cars/cars-grid/cars-grid.component';
import {CarsGridPubComponent} from './cars-market/cars-grid-pub/cars-grid-pub.component';
import {CarDialogOverviewComponent} from './cars/car-dialog-overview/car-dialog-overview.component';
import {CarModalComponent} from './cars/car-modal/car-modal.component';
import {ImageDialogComponent} from './cars/image-dialog/image-dialog.component';
import {ThankYouDialogComponent} from './cars/car-modal/thank-you-dialog.component';
import {CarsGridComponent as MyCarsGridComponent} from './cars/my-list/cars-grid/cars-grid.component';
import {MyCarModalComponent} from './cars/my-list/my-car-modal/my-car-modal.component';
import {ShareCarComponent} from './cars/share-car/share-car.component';
import {ConfirmationSaveDialogComponent} from './cars/my-list/my-car-modal/confirmation-save.component';
import {RangeSlideDirective} from './directives/range/range.directive';
import {LazyLoadDirective} from './directives/lazy-load.directive';
import {ScrollToTopDirective} from './directives/app-scroll-to-top.directive';
import {EditableListComponent} from './cars/editable-list/editable-list.component';
import {MatCardModule} from '@angular/material/card';
import {MatRippleModule} from '@angular/material/core';
import {CarouselModule} from 'primeng/carousel';
import {NgSelectModule} from '@ng-select/ng-select';
import {MatBottomSheetModule} from '@angular/material/bottom-sheet';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {ConfirmationSourceDialogComponent} from './cars/my-list/my-car-modal/confirmation-source.component';
import {AccesabilityStatementComponent} from './accesability-statement/accesability-statement.component';
import {StellantisLayoutComponent} from './stellantis-layout/stellantis-layout.component';

import {StellantisModule} from './stellantis/stellantis.module';
import {LoginModalComponent} from './auth/login/stellantis/login-modal.component';
import {DeliveryDialogComponent} from './stellantis-layout/delivery-dialog/delivery-dialog.component';
import {CarModalStellantisComponent} from './cars/car-modal-stellantis/car-modal-stellantis.component';
import {ConfirmationDialogStellantisComponent} from './cars/car-modal-stellantis/confirmation-cancel-stellantis.component';
import {ThankYouDialogStellantisComponent} from './cars/car-modal-stellantis/thank-you-dialog-stellantis.component';
import {CarsGridCollComponent} from './cars/cars-grid-coll/cars-grid-coll.component';
import {MatTabsModule} from '@angular/material/tabs';
import {AcceptTermsComponent} from './cars/accept-terms/accept-terms.component';
import {CarsGridCollPubComponent} from './cars-market/cars-grid-coll-pub/cars-grid-coll-pub.component';
import {CarOverviewComponent} from './cars/car-overview/car-overview.component';
import {UserInfoModalComponent} from './cars/user-info/user-info-modal.component';
import {ShareComponent} from './shared/SocialSharing/share.component';
import {PaymentModule} from './payment/payment.module';
import {CarsGridSellerComponent} from './cars-seller/cars-grid-seller/cars-grid-seller.component';
import {ThankYouDialogSellerComponent} from './cars/car-modal-seller/thank-you-dialog-seller.component';
import {ConfirmationSellerDialogComponent} from './cars/car-modal/confirmation-cancel.component';
import {CarModalSellerComponent} from './cars/car-modal-seller/car-modal-seller.component';
import {ArbitrageTermsComponent} from './cars-seller/arbitrage-terms/arbitrage-terms.component';
import {ArbitrageTermsUaeComponent} from './cars-seller-uae/arbitrage-terms-uae/arbitrage-terms-uae.component';
import {ConfirmationSellerUaeDialogComponent} from './cars-seller-uae/car-modal/confirmation-cancel-uae.component';
import {ThankYouUaeDialogComponent} from './cars-seller-uae/car-modal/thank-you-uae-dialog.component';
import {CarsUaeModule} from './cars-seller-uae/cars-uae.module';
import {CarsGaModule} from './cars-seller-ga/cars-ga.module';
import {CarsNigeriaModule} from './cars-seller-nigeria/cars-nigeria.module';
import {ArbitrageTermsGaComponent} from './cars-seller-ga/arbitrage-terms-ga/arbitrage-terms-ga.component';
import {ThankYouGaDialogComponent} from './cars-seller-ga/car-modal/thank-you-ga-dialog.component';
import {ArbitrageTermsNigeriaComponent} from './cars-seller-nigeria/arbitrage-terms-nigeria/arbitrage-terms-nigeria.component';
import {ConfirmationSellerNigeriaDialogComponent} from './cars-seller-nigeria/car-modal/confirmation-cancel-nigeria.component';
import {ThankYouNigeriaDialogComponent} from './cars-seller-nigeria/car-modal/thank-you-nigeria-dialog.component';

// // configure Bugsnag asap
// Bugsnag.start({ apiKey: 'a5259d1e2ce4fbed751ab9d86112b553' })
//
// // create a factory which will return the Bugsnag error handler
// export function errorHandlerFactory() {
//   return new BugsnagErrorHandler()
// }

registerLocaleData(he);

// Translation loader factory
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${environment.buildHash}`);
}

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: 'slickchain.com'
  },
  position: 'bottom',
  theme: 'classic',
  palette: {
    popup: {
      background: '#304785',
      text: '#ffffff',
      link: '#ffffff'
    },
    button: {
      background: '#f1d600',
      text: '#000000',
      border: 'transparent'
    }
  },
  type: 'info',
  content: {
    message: 'אתר זה עושה שימוש בעוגיות',
    dismiss: 'מקבל',
    deny: 'Refuse cookies',
    link: '',
    href: '',
    policy: 'Cookie Policy'
  }
};

@NgModule({
    declarations: [
        AppComponent,
        FooterComponent,
        cookies,
        TermsComponent,
        VehicleEuStandardInfoComponent,
        AcceptTermsEuComponent,
        CarsMarketLoginComponent,
        TextEditableComponent,
        CarsGridComponent,
        CarsGridCollComponent,
        CarsGridPubComponent,
        CarsGridSellerComponent,
        CarDialogOverviewComponent,
        CarModalComponent,
        ImageDialogComponent,
        ThankYouDialogComponent,
        MyCarsGridComponent,
        MyCarModalComponent,
        ShareCarComponent,
        ConfirmationSaveDialogComponent,
        RangeSlideDirective,
        LazyLoadDirective,
        ScrollToTopDirective,
        EditableListComponent,
        ConfirmationSourceDialogComponent,
        AccesabilityStatementComponent,
        StellantisLayoutComponent,
      LoginModalComponent,
      DeliveryDialogComponent,
      CarModalStellantisComponent,
      CarModalSellerComponent,
      ConfirmationDialogStellantisComponent,
      ThankYouDialogStellantisComponent,
      ThankYouDialogSellerComponent,
      ConfirmationSellerDialogComponent,
      AcceptTermsComponent,
      CarsGridCollPubComponent,
      CarOverviewComponent,
      UserInfoModalComponent,
      ShareComponent,
      ArbitrageTermsComponent,
      ArbitrageTermsUaeComponent,
      ArbitrageTermsGaComponent,
      ArbitrageTermsNigeriaComponent,
      ConfirmationSellerUaeDialogComponent,
      ConfirmationSellerNigeriaDialogComponent,
      ThankYouUaeDialogComponent,
      ThankYouGaDialogComponent,
      ThankYouNigeriaDialogComponent,
    ],
    imports: [
        BrowserModule,
        FormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        PagesModule,
        HeaderModule,
        FlexLayoutModule,
        HttpClientModule,
        StoreModule.forRoot(reducers, {
            metaReducers
        }),
        NgxMaskModule.forRoot(),
        StoreDevtoolsModule.instrument({maxAge: 25, logOnly: environment.production}),
        FontAwesomeModule,
        AngularSvgIconModule.forRoot(),
        GooglePlaceModule,
        NgcCookieConsentModule.forRoot(cookieConfig),
        EffectsModule.forRoot(),
        EffectsModule.forFeature([RegisterEffects, LoginEffects, CalculatorEffects, OrdersEffects, NotificationEffects, ConversationEffects]),
        NgxUiLoaderModule.forRoot({
            minTime: 300
        }),
        NgxUiLoaderRouterModule.forRoot({}),
        InViewportModule,
        CountUpModule,
        CarsUaeModule,
        CarsGaModule,
        CarsNigeriaModule,
        StoreModule.forFeature(fromLogin.loginFeatureKey, fromLogin.reducer),
        StoreModule.forFeature(fromCalculator.calculatorFeatureKey, fromCalculator.reducer),
        StoreModule.forFeature(fromOrders.ordersFeatureKey, fromOrders.reducer),
        StoreModule.forFeature(fromNotification.notificationsFeatureKey, fromNotification.reducer),
        StoreModule.forFeature(fromConversation.conversationsFeatureKey, fromConversation.reducer),
        MomentModule,
        MatIconModule,
        FreightSharedModule,
        BottomNavModule,
        SidebarModule.forRoot(),
        ShareIconsModule,
        AgmCoreModule.forRoot({
            apiKey: 'AIzaSyCjb2wktsp6sNabiaTvfFvCsyAQiMTwp_8'
        }),
        NbThemeModule.forRoot({name: 'default'}, [], [], NbLayoutDirection.RTL),
        NbLayoutModule,
        NbDialogModule.forRoot(),
        NgxSliderModule,
        MatInputModule,
        MatTableModule,
        MatAutocompleteModule,
        MatSelectModule,
        ReactiveFormsModule,
        DropdownModule,
        MatSortModule,
        MatButtonModule,
        RecaptchaModule,
        MatTooltipModule,
        NbEvaIconsModule,
        CommonModule,
        // CarsRoutingModule,
        MatCardModule,
        MatButtonModule,
        FontAwesomeModule,
        MatRippleModule,
        MatDialogModule,
        CarouselModule,
        NbCardModule,
        NbIconModule,
        NbButtonModule,
        NbDialogModule.forRoot(),
        NbIconModule,
        NbEvaIconsModule,
        NbInputModule,
        NgSelectModule,
        NbFormFieldModule,
        ReactiveFormsModule,
        FreightSharedModule,
        NbToggleModule,
        MatIconModule,
        AngularSvgIconModule,
        NbTooltipModule,
        MatBottomSheetModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        ToastModule,
        MatRadioModule,
        NgxSliderModule,
        NgxSliderModule,
        FormsModule,
        MatTableModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        StellantisModule,
        MatTabsModule,
        PaymentModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        })
    ],
    providers: [
        CookieService,
        {provide: LOCALE_ID, useValue: 'he-IL'},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        { provide: MAT_DIALOG_DATA, useValue: {} },
        { provide: MatDialogRef, useValue: {} },
        //{ provide: ErrorHandler, useFactory: errorHandlerFactory }
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {
            },
            deps: [Sentry.TraceService],
            multi: true,
        },
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true},
        RecaptchaLoaderService
    ],
    exports: [
        TextEditableComponent
    ],

  bootstrap: [AppComponent]
})

export class AppModule {

}
